import * as React from 'react'
import { ReactElement } from 'react'
import { useFormikContext } from 'formik'
import { Switch, SwitchOnChangeData, SwitchProps } from '@fluentui/react-components'
import FormikField from '../../../../components/Formik/FormikField'
import FormikComboboxField from '../../../../components/Formik/FormikComboboxField'
import FormikTextareaField from '../../../../components/Formik/FormikTextareaField'
import UserCombobox from '../../../../components/Combobox/UserCombobox'
import { SubComboboxProps } from '../../../../components/Combobox/BaseCombobox'
import { UserListItemDto } from '../../../../config/userConfig'
import FolderPathField from '../../../components/Input/FolderPathField'
import { useFolderUsageCheck } from '../../../components/Input/useFolderUsageCheck'

type Props = {
    /** Suggested folder name (only when no document root was set yet). */
    suggestedName?: string
    /** The party being edited, so it is not reported as occupying its own folder. */
    partyId?: string
}

/**
 * CRM details of a party (only when the CRM module is active). When the CRM-relation switch is off,
 * the account manager and document root are cleared. Behaviour mirrors the legacy party form.
 */
export default function CrmSection(props: Props): ReactElement {
    const { initialValues, values, setFieldValue, setFieldError, submitCount } = useFormikContext<any>()
    const checkUsage = useFolderUsageCheck({ excludeParty: props.partyId })

    return (
        <>
            <FormikField<SwitchProps, any>
                orientation="horizontal"
                labelKey="party.edit.crm.crmrelation"
                fieldName="crmRelation"
                component={Switch}
                componentProps={{
                    checked: values.crmRelation,
                    onChange: (_, data: SwitchOnChangeData) => {
                        setFieldValue('crmRelation', data.checked)
                        if (!data.checked) {
                            setFieldValue('accountManagerId', undefined)
                            setFieldValue('documentRootId', undefined)
                            // Drop the intent too, or switching CRM off would still create the folder.
                            setFieldValue('documentRootIntent', undefined)
                        }
                    },
                }}
            />
            {values.crmRelation && (
                <FormikComboboxField<any, SubComboboxProps<UserListItemDto>>
                    required
                    orientation="horizontal"
                    labelKey="party.edit.crm.accountmanagerid"
                    fieldName="accountManagerId"
                    component={UserCombobox}
                    componentProps={{ placeholderKey: 'party.edit.crm.accountmanagerid.placeholder' }}
                />
            )}
            {values.crmRelation && (
                <FolderPathField
                    required
                    itemId={initialValues.documentRootId}
                    suggestedName={props.suggestedName}
                    checkUsage={checkUsage}
                    // Until the relation is saved an untouched, still-empty folder name is not a
                    // mistake yet, so the field only turns red from the first submit attempt on.
                    validateEmpty={submitCount > 0}
                    onChange={(intent, errorMessage) => {
                        // The intent is carried out by applyFolderIntent on submit, so nothing is
                        // written to SharePoint while the user is still editing the form.
                        // setFieldError, not setErrors: replacing the whole error object here wiped
                        // every other field's validation result from the form (HAM-4343).
                        setFieldError('documentRootId', errorMessage)
                        setFieldValue('documentRootIntent', intent)
                    }}
                />
            )}
            {values.crmRelation && (
                <FormikTextareaField<any>
                    orientation="horizontal"
                    labelKey="party.edit.crm.description"
                    fieldName="description"
                    componentProps={{ rows: 5 }}
                />
            )}
        </>
    )
}
